import React from "react";

import { formatKstShort } from "../../utils/time";
import { getStrategyMeta } from "../../data/strategyMeta";
import VerdictBadge from "./VerdictBadge";
import {
    readStrategyFields,
    classifyStrategy,
    NewBadge,
    LOSS_WARN_KRW,
} from "./PaperStrategyCard";
import { stripTrailingCoin, variantLabel } from "./PaperStrategyGroupCard";
import { familyBadge } from "../../data/strategyFamily";
import { KNOWN_COINS } from "../../data/strategyGrouping";

/**
 * PaperStrategyListView — 전략 목록의 고밀도 리스트(행) 뷰. (DAS-142)
 *
 * 카드 뷰(전략당 ~200px)와 같은 데이터를 행당 ~48px 로 압축해 스크롤 길이를
 * 1/4 수준으로 줄인다. 판정·PnL·거래·WR·PF 를 세로로 정렬된 컬럼으로 두어
 * "어느 가설을 실전으로 고도화할지" 비교 스캔에 최적화한 보기다.
 *
 * - 단일 전략 행: 클릭 → 디테일 페이지
 * - 그룹(패밀리) 행: 클릭 → 멤버 행 펼침/접힘, 멤버 행 클릭 → 디테일
 * - 모바일(<sm): 2줄 컴팩트 행 (이름+판정 / 지표 한 줄)
 * - 데스크톱(sm+): 테이블형 컬럼 정렬 + 헤더 행
 *
 * 정렬·필터는 호출부(PaperOverviewPage)가 적용한 순서를 그대로 따른다.
 */
export default function PaperStrategyListView({
    strategies,
    onClick,
    adoptedIds,
    newIds,
    verdictMap,
    showFamily = false,
}) {
    const list = Array.isArray(strategies) ? strategies : [];
    if (list.length === 0) return null;
    const adoptedSet = adoptedIds instanceof Set ? adoptedIds : new Set();
    const newSet = newIds instanceof Set ? newIds : new Set();

    return (
        <div className="bg-surface-card border border-surface-border rounded-lg">
            <HeaderRow />
            <ul className="divide-y divide-surface-border-light">
                {list.map((s) =>
                    s?.isGroup ? (
                        <GroupRow
                            key={`group:${s.baseId}`}
                            group={s}
                            onMemberClick={onClick}
                            adoptedSet={adoptedSet}
                            newSet={newSet}
                            verdict={verdictMap instanceof Map ? verdictMap.get(s.baseId) : null}
                            showFamily={showFamily}
                        />
                    ) : (
                        <SingleRow
                            key={readStrategyFields(s).id}
                            strategy={s}
                            onClick={onClick}
                            adopted={adoptedSet.has(readStrategyFields(s).id)}
                            isNew={newSet.has(readStrategyFields(s).id)}
                            showFamily={showFamily}
                        />
                    ),
                )}
            </ul>
        </div>
    );
}

/** 컬럼 헤더 (sm+ 전용 — 모바일은 행 내 라벨로 대체). */
function HeaderRow() {
    return (
        <div
            aria-hidden="true"
            className="hidden sm:flex items-center gap-3 px-3 pt-2 pb-1.5 text-xs text-content-muted border-b border-surface-border-light"
        >
            <span className="flex-1 min-w-0">전략</span>
            <span className="w-24 shrink-0">판정</span>
            <span className="w-24 shrink-0 text-right">PnL (KRW)</span>
            <span className="w-12 shrink-0 text-right">거래</span>
            <span className="w-12 shrink-0 text-right">WR</span>
            <span className="w-12 shrink-0 text-right">PF</span>
            <span className="w-24 shrink-0 text-right hidden lg:block">최근 거래</span>
        </div>
    );
}

const STATUS_DOT = {
    active: "bg-brand animate-pulse",
    live: "bg-gain",
    idle: "bg-warning",
    waiting: "bg-content-muted",
};
const STATUS_LABEL = {
    active: "진입중",
    live: "활성",
    idle: "휴면",
    waiting: "신호 대기",
};

/** 이름 영역 (dot + ⭐ + 신규 + 이름 + 보조 칩) — 단일/그룹 공용. */
function NameCell({ bucket, adopted, isNew, name, chips }) {
    return (
        <span className="flex items-center gap-1.5 flex-1 min-w-0">
            <span
                aria-hidden="true"
                title={STATUS_LABEL[bucket]}
                className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                    STATUS_DOT[bucket] ?? STATUS_DOT.waiting
                }`}
            />
            {adopted && (
                <span
                    className="text-warning shrink-0"
                    title="채택됨 (우수 전략 북마크)"
                    aria-hidden="true"
                >
                    ⭐
                </span>
            )}
            {isNew && <NewBadge />}
            <span className="text-sm font-medium text-content truncate">{name}</span>
            {chips}
        </span>
    );
}

/** 지표 셀 묶음 — sm+ 테이블 컬럼 + 모바일 한 줄 요약을 함께 렌더. */
function MetricCells({ fields, verdictProps }) {
    const { pnl, trades, wr, pf, lastTradeTs } = fields;
    const pnlClass =
        pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content-secondary";
    const pnlText = pnl.toLocaleString(undefined, { maximumFractionDigits: 0 });
    const pfText = pf >= 0 ? pf.toFixed(2) : "—";
    return (
        <>
            {/* 데스크톱 컬럼 */}
            <span className="hidden sm:flex items-center gap-3 shrink-0 text-xs">
                <span className="w-24 shrink-0">
                    <VerdictBadge {...verdictProps} showScore={false} />
                </span>
                <span className={`w-24 shrink-0 text-right font-semibold tabular-nums ${pnlClass}`}>
                    {pnlText}
                </span>
                <span className="w-12 shrink-0 text-right tabular-nums text-content">
                    {trades}
                </span>
                <span className="w-12 shrink-0 text-right tabular-nums text-content">
                    {(wr * 100).toFixed(0)}%
                </span>
                <span className="w-12 shrink-0 text-right tabular-nums text-content">
                    {pfText}
                </span>
                <span className="w-24 shrink-0 text-right text-content-muted hidden lg:block">
                    {lastTradeTs ? formatKstShort(lastTradeTs) : "—"}
                </span>
            </span>
            {/* 모바일 요약 줄 */}
            <span className="flex sm:hidden items-center flex-wrap gap-x-2 gap-y-1 pl-3 text-xs">
                <VerdictBadge {...verdictProps} showScore={false} />
                <span className={`font-semibold tabular-nums ${pnlClass}`}>{pnlText}</span>
                <span className="text-content-muted">
                    {trades}건 · WR {(wr * 100).toFixed(0)}% · PF {pfText}
                </span>
            </span>
        </>
    );
}

function familyChip(family) {
    if (!family) return null;
    return (
        <span className="hidden md:inline-flex items-center gap-1 text-xs text-content-muted bg-surface-elevated px-1.5 py-0.5 rounded shrink-0">
            <span aria-hidden="true">{family.glyph}</span>
            {family.label}
        </span>
    );
}

function SingleRow({ strategy, onClick, adopted, isNew, showFamily }) {
    const f = readStrategyFields(strategy);
    const meta = getStrategyMeta(f.id);
    const displayName = meta?.name || f.id;
    const bucket = classifyStrategy(f);
    const lossWarn = f.openCount === 0 && f.pnl <= LOSS_WARN_KRW;
    return (
        <li>
            <button
                type="button"
                onClick={() => onClick && onClick(f.id)}
                aria-label={`${displayName} 전략 디테일 보기 (상태: ${STATUS_LABEL[bucket]}${
                    isNew ? ", 신규" : ""
                }${lossWarn ? ", 손실 경고" : ""}${adopted ? ", 채택됨" : ""})`}
                className={`w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-3 py-2 min-h-11 text-left hover:bg-surface-hover transition-colors ${
                    lossWarn ? "border-l-2 border-l-loss" : ""
                }`}
            >
                <NameCell
                    bucket={bucket}
                    adopted={adopted}
                    isNew={isNew}
                    name={displayName}
                    chips={showFamily ? familyChip(familyBadge(strategy)) : null}
                />
                <MetricCells fields={f} verdictProps={{ strategy, fields: f }} />
            </button>
        </li>
    );
}

function GroupRow({ group, onMemberClick, adoptedSet, newSet, verdict, showFamily }) {
    const [open, setOpen] = React.useState(false);
    const f = readStrategyFields(group);
    const members = group.members ?? [];
    const memberCount = group.memberCount ?? members.length;
    const bucket = classifyStrategy(f);
    const unit = variantLabel(group.variantAxis);

    const firstMeta = members.length
        ? getStrategyMeta(readStrategyFields(members[0]).id)
        : null;
    const displayName = firstMeta?.name ? stripTrailingCoin(firstMeta.name) : f.id;

    const adoptedCount = members.filter((m) =>
        adoptedSet.has(readStrategyFields(m).id),
    ).length;
    const groupIsNew =
        newSet.has(group.baseId) ||
        members.some((m) => newSet.has(readStrategyFields(m).id));

    return (
        <li>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-label={`${displayName} 패밀리 (${memberCount} ${unit.unit}) ${
                    open ? "접기" : "펼치기"
                }`}
                className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-3 py-2 min-h-11 text-left hover:bg-surface-hover transition-colors"
            >
                <NameCell
                    bucket={bucket}
                    adopted={adoptedCount > 0}
                    isNew={groupIsNew}
                    name={displayName}
                    chips={
                        <>
                            <span
                                className="inline-flex items-center gap-0.5 text-xs text-brand-dark bg-brand-subtle px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap"
                                title={unit.title}
                            >
                                <Chevron open={open} />
                                {memberCount} {unit.unit}
                            </span>
                            {showFamily ? familyChip(familyBadge(group)) : null}
                        </>
                    }
                />
                <MetricCells
                    fields={f}
                    verdictProps={{ strategy: group, verdict, fields: f }}
                />
            </button>
            {open && (
                <ul className="pb-1 divide-y divide-surface-border-light border-t border-surface-border-light bg-surface">
                    {members.map((m) => (
                        <MemberRow
                            key={readStrategyFields(m).id}
                            member={m}
                            onClick={onMemberClick}
                            adopted={adoptedSet.has(readStrategyFields(m).id)}
                            isNew={newSet.has(readStrategyFields(m).id)}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

/** 그룹 펼침 시 멤버(변형) 1행 — 들여쓰기 + 변형 토큰 강조. */
function MemberRow({ member, onClick, adopted, isNew }) {
    const f = readStrategyFields(member);
    const bucket = classifyStrategy(f);
    const variant = variantToken(f.id);
    return (
        <li>
            <button
                type="button"
                onClick={() => onClick && onClick(f.id)}
                aria-label={`${f.id} 디테일 보기 (상태: ${STATUS_LABEL[bucket]}${
                    isNew ? ", 신규" : ""
                })`}
                className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 py-2 pl-7 pr-3 min-h-11 text-left hover:bg-surface-hover transition-colors"
            >
                <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span
                        aria-hidden="true"
                        title={STATUS_LABEL[bucket]}
                        className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                            STATUS_DOT[bucket] ?? STATUS_DOT.waiting
                        }`}
                    />
                    {adopted && (
                        <span className="text-warning shrink-0" aria-hidden="true">
                            ⭐
                        </span>
                    )}
                    {isNew && (
                        <span
                            className="text-brand-dark shrink-0"
                            title="최근 추가된 변형"
                            aria-hidden="true"
                        >
                            ✦
                        </span>
                    )}
                    <span className="text-xs font-semibold text-content shrink-0">
                        {variant}
                    </span>
                    <span className="text-xs text-content-muted truncate">{f.id}</span>
                </span>
                <MetricCells fields={f} verdictProps={{ strategy: member, fields: f }} />
            </button>
        </li>
    );
}

function Chevron({ open }) {
    return (
        <svg
            aria-hidden="true"
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

/**
 * strategy_id 끝의 변형 토큰(코인/파라미터)을 표시 라벨로.
 * 코인이면 대문자("h318_bnb" → "BNB"), 파라미터면 그대로("h1106_tight" → "tight"),
 * 접미사 없으면 "base".
 */
function variantToken(id) {
    const idx = typeof id === "string" ? id.lastIndexOf("_") : -1;
    if (idx <= 0 || idx === id.length - 1) return "base";
    const token = id.slice(idx + 1);
    return KNOWN_COINS.has(token.toLowerCase()) ? token.toUpperCase() : token;
}
